import React from 'react';
import { i18n } from 'config/locales';
import { Summary, NameSummary } from 'components/Summary';
import Field from 'components/Form/Field';

import foreign, { FOREIGN, FOREIGN_ACTIVITIES_SUPPORT } from 'config/formSections/foreign';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import ErrorMessage from 'components/ErrorMessage';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignSupportAddressNameFields } from 'constants/enums/people';
import { isPVQForm } from 'helpers/form';
import SupportItem from './SupportItem';

const sectionConfig = {
  key: FOREIGN_ACTIVITIES_SUPPORT.key,
  section: FOREIGN.name,
  store: FOREIGN.store,
  subsection: FOREIGN_ACTIVITIES_SUPPORT.name,
  storeKey: FOREIGN_ACTIVITIES_SUPPORT.storeKey,
};
export class Support extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      HasForeignSupport: this.props.HasForeignSupport,
      List: this.props.List,
      ...queue,
    });
  };

  updateHasForeignSupport = (values) => {
    this.update({
      HasForeignSupport: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const name = NameSummary(o.Name);
    return Summary({
      type: i18n.t('foreign.activities.support.collection.summary.item'),
      index,
      left: name,
      placeholder: i18n.t('foreign.activities.support.collection.summary.unknown'),
    });
  };

  render() {
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignSupportAddressNameFields);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPVQ = isPVQForm(this.props.formType);
    return isPVQ ? (

      <div
        className="section-content foreign-activities-support"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_ACTIVITIES_SUPPORT.key}
      >
        <h1 className="section-header">{i18n.t('foreign.subsection.activities.support')}</h1>

        <Branch
          name="has_foreign_support"
          label={i18n.m('foreign.pvq.activities.supportTitle')}
          labelSize="h4"
          {...this.props.HasForeignSupport}
          warning
          onUpdate={this.updateHasForeignSupport}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignSupport"
        />

        <Show when={this.props.HasForeignSupport.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.activities.support.collection.summary.title')}
            appendTitle="Do you have another instance in which you've provided financial support to a foreign national in the past five years? "
            appendLabel={i18n.t('foreign.activities.support.collection.append')}
            required={this.props.required}
            scrollToBottom={this.props.scrollToBottom}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <SupportItem
              name="Item"
              bind
              dispatch={this.props.dispatch}
              addressBooks={this.props.addressBooks}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>
    ) : (

      <div
        className="section-content foreign-activities-support"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_ACTIVITIES_SUPPORT.key}
      >
        <h1 className="section-header">{i18n.t('foreign.subsection.activities.support')}</h1>
        <Branch
          name="has_foreign_support"
          label={i18n.t('foreign.activities.support.heading.title')}
          labelSize="h4"
          {...this.props.HasForeignSupport}
          warning
          onUpdate={this.updateHasForeignSupport}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignSupport"
        />

        <Show when={this.props.HasForeignSupport.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.activities.support.collection.summary.title')}
            appendTitle={i18n.t('foreign.activities.support.collection.appendTitle')}
            appendLabel={i18n.t('foreign.activities.support.collection.append')}
            required={this.props.required}
            scrollToBottom={this.props.scrollToBottom}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <SupportItem
              name="Item"
              bind
              dispatch={this.props.dispatch}
              addressBooks={this.props.addressBooks}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Support.defaultProps = {
  name: 'Support',
  HasForeignSupport: {},
  List: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'activities/support',
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  errors: [],
};

export default connectSubsection(Support, sectionConfig);
